import React from "react";

type SyncTicketType = {
	id: string;
	name: string;
	soldTickets: number;
	syncPartnerTicketId?: string;
	syncPartnerTicketTitle?: string;
};

interface TicketSyncSettingsProps {
	enabled: boolean;
	partnerEventId: string;
	partnerEventTitle?: string;
	ticketTypes: SyncTicketType[];
	onEnabledChange: (enabled: boolean) => void;
	onPartnerEventIdChange: (value: string) => void;
	onTicketMappingChange: (ticketId: string, value: string) => void;
}

const TicketSyncSettings: React.FC<TicketSyncSettingsProps> = ({
	enabled,
	partnerEventId,
	partnerEventTitle,
	ticketTypes,
	onEnabledChange,
	onPartnerEventIdChange,
	onTicketMappingChange,
}) => {
	return (
		<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-amber-200 dark:border-amber-700/50 p-6 space-y-5">
			<div className="space-y-2">
				<div className="flex items-start justify-between gap-4">
					<div>
						<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
							Webサイト販売同期
						</h3>
						<p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
							`ticket.blackadder.co.jp` と `miyatako-hei.com` の売上を相互反映します。
						</p>
					</div>
					<label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
						<input
							type="checkbox"
							className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
							checked={enabled}
							onChange={(event) => onEnabledChange(event.target.checked)}
						/>
						同期を有効化
					</label>
				</div>
				<div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-100">
					miyatako-hei.com 側の座席指定チケットはまだ同期対象にできません。
				</div>
			</div>

			<div className="grid gap-4 md:grid-cols-[minmax(0,260px)_1fr] md:items-end">
				<label className="block text-sm text-gray-700 dark:text-gray-300">
					<span className="mb-2 block font-medium">miyatako 側イベントID</span>
					<input
						type="number"
						min="1"
						value={partnerEventId}
						onChange={(event) => onPartnerEventIdChange(event.target.value)}
						disabled={!enabled}
						className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200 disabled:cursor-not-allowed disabled:bg-gray-100 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:focus:border-amber-400 dark:focus:ring-amber-900/50 dark:disabled:bg-gray-700"
						placeholder="例: 123"
					/>
				</label>
				<div className="text-sm text-gray-600 dark:text-gray-400">
					{enabled && partnerEventTitle ? (
						<span>保存済みの同期先イベント: {partnerEventTitle}</span>
					) : (
						<span>イベント保存時に miyatako 側イベントとチケットIDを検証します。</span>
					)}
				</div>
			</div>

			<div className="space-y-3">
				<div>
					<h4 className="text-sm font-semibold text-gray-900 dark:text-white">
						チケット対応表
					</h4>
					<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
						blackadder 側の各チケットに対して、miyatako 側のチケットIDを1つずつ指定してください。
					</p>
				</div>
				<div className="space-y-3">
					{ticketTypes.map((ticketType) => (
						<div
							key={ticketType.id}
							className="grid gap-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700 md:grid-cols-[minmax(0,1fr)_220px]"
						>
							<div className="space-y-1">
								<p className="text-sm font-medium text-gray-900 dark:text-white">
									{ticketType.name || "名称未設定のチケット"}
								</p>
								<p className="text-xs text-gray-500 dark:text-gray-400">
									販売済み: {ticketType.soldTickets} 枚
								</p>
								{ticketType.syncPartnerTicketTitle ? (
									<p className="text-xs text-amber-700 dark:text-amber-300">
										保存済み同期先: {ticketType.syncPartnerTicketTitle}
									</p>
								) : null}
							</div>
							<label className="block text-sm text-gray-700 dark:text-gray-300">
								<span className="mb-2 block font-medium">miyatako 側チケットID</span>
								<input
									type="number"
									min="1"
									value={ticketType.syncPartnerTicketId ?? ""}
									onChange={(event) =>
										onTicketMappingChange(ticketType.id, event.target.value)
									}
									disabled={!enabled}
									className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200 disabled:cursor-not-allowed disabled:bg-gray-100 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:focus:border-amber-400 dark:focus:ring-amber-900/50 dark:disabled:bg-gray-700"
									placeholder="例: 456"
								/>
							</label>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

export default TicketSyncSettings;
